import React, { useRef, useEffect, useState, useCallback } from 'react';

// Sabit (fixed, object-cover) arka plan görselini bir kez küçültülmüş bir canvas'a
// çizip, viewport koordinatından o noktadaki parlaklığı (luminance) örneklemeyi sağlar.
function useBackgroundSampler(imageUrl) {
    const dataRef = useRef(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        setReady(false);
        dataRef.current = null;
        if (!imageUrl) return;

        let cancelled = false;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            if (cancelled) return;
            try {
                const maxSide = 480; // örnekleme için yeterli, performanslı
                const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
                const w = Math.max(1, Math.round(img.naturalWidth * scale));
                const h = Math.max(1, Math.round(img.naturalHeight * scale));
                const canvas = document.createElement('canvas');
                canvas.width = w; canvas.height = h;
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                ctx.drawImage(img, 0, 0, w, h);
                dataRef.current = { ctx, w, h, natW: img.naturalWidth, natH: img.naturalHeight };
                setReady(true);
            } catch {
                dataRef.current = null;
                setReady(false);
            }
        };
        img.onerror = () => { if (!cancelled) { dataRef.current = null; setReady(false); } };
        img.src = imageUrl;
        return () => { cancelled = true; };
    }, [imageUrl]);

    // viewport (vx, vy) -> cover haritalama -> 0..1 relative luminance
    const sampleLuminance = useCallback((vx, vy) => {
        const s = dataRef.current;
        if (!s) return null;
        const vw = window.innerWidth, vh = window.innerHeight;
        const scale = Math.max(vw / s.natW, vh / s.natH); // background-size: cover
        const dispW = s.natW * scale, dispH = s.natH * scale;
        const offX = (dispW - vw) / 2, offY = (dispH - vh) / 2;
        const ixNat = (vx + offX) / scale;
        const iyNat = (vy + offY) / scale;
        const cx = Math.max(0, Math.min(s.w - 1, Math.round((ixNat / s.natW) * s.w)));
        const cy = Math.max(0, Math.min(s.h - 1, Math.round((iyNat / s.natH) * s.h)));
        try {
            const d = s.ctx.getImageData(cx, cy, 1, 1).data;
            if (d[3] === 0) return null; // şeffaf piksel -> örnekleme yok
            return (0.2126 * d[0] + 0.7152 * d[1] + 0.0722 * d[2]) / 255;
        } catch {
            return null;
        }
    }, [ready]);

    return { ready, sampleLuminance };
}

/**
 * Metni karakter karakter <span>'lara böler ve (varsa) arka plan görselinin
 * arkasındaki parlaklığa göre her karaktere zıt renk (koyu/açık) verir.
 * Arka plan görseli yoksa veya örneklenemezse `fallbackColor` kullanılır.
 */
export default function AdaptiveText({
    text,
    as: Tag = 'span',
    className = '',
    imageUrl = null,
    fallbackColor,
    darkColor = '#141414',
    lightColor = '#ffffff',
    threshold = 0.55,
    style,
    ...rest
}) {
    const { ready, sampleLuminance } = useBackgroundSampler(imageUrl);
    const refs = useRef([]);
    const [colors, setColors] = useState({});
    const chars = Array.from(text == null ? '' : String(text));

    const recompute = useCallback(() => {
        if (!imageUrl || !ready) { setColors({}); return; }
        const next = {};
        for (let i = 0; i < refs.current.length; i++) {
            const el = refs.current[i];
            if (!el) continue;
            const r = el.getBoundingClientRect();
            if (r.width === 0 && r.height === 0) continue;
            const lum = sampleLuminance(r.left + r.width / 2, r.top + r.height / 2);
            if (lum == null) continue;
            next[i] = lum > threshold ? darkColor : lightColor;
        }
        setColors(next);
    }, [imageUrl, ready, sampleLuminance, threshold, darkColor, lightColor]);

    useEffect(() => {
        // Layout / ölçekleme oturduktan sonra hesapla
        const raf = requestAnimationFrame(recompute);
        const t = setTimeout(recompute, 350);
        window.addEventListener('resize', recompute);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t);
            window.removeEventListener('resize', recompute);
        };
    }, [recompute, text]);

    return (
        <Tag className={className} style={style} {...rest}>
            {chars.map((ch, i) => (
                <span
                    key={i}
                    ref={(el) => { refs.current[i] = el; }}
                    style={{
                        color: (imageUrl && ready && colors[i]) ? colors[i] : fallbackColor,
                        transition: 'color 0.45s ease',
                    }}
                >
                    {ch}
                </span>
            ))}
        </Tag>
    );
}
